// RFC-0008 T4: 首页 — deck 列表 + 模板示例画廊
// 设计：进来直接看到列表 + 18 张模板封面卡（点封面进 /demo 翻页预览，点"用这个风格"按钮 addReference 到小北）
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listDecks, deleteDeck, type DeckMeta } from '../lib/api';
import { useUser } from '../components/user-provider';
import { TEMPLATES, type TemplateMeta, buildTemplateChatInput, buildBlankChatInput } from '../lib/templates';
import { TEMPLATE_COVERS } from '../lib/template-covers';
import { installArtifact, isArtifactInstalled, setChatInput } from '../lib/north-sdk';

const SKILL_NAME = '小北PPT技能';

// skill 安装状态机：
//   checking  — 后台静默检查是否已装（不弹窗、不挡操作）
//   installing— 确认未装，正在安装（弹窗显示）
//   ready     — 已就绪
//   error     — 安装失败（弹窗显示，可重试）
type SkillStatus = 'checking' | 'installing' | 'ready' | 'error';

export function HomeRoute() {
  const { user } = useUser();
  const appUserId = user?.userId ?? null;
  const navigate = useNavigate();
  const [decks, setDecks] = useState<DeckMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [skillStatus, setSkillStatus] = useState<SkillStatus>('checking');
  // 「准备技能」弹窗：默认不弹。仅在确认需要安装/安装失败时才弹，
  // 已安装（绝大多数情况）后台静默检查，不打扰用户。
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  // 运行时真实 skill artifactId（dev/prod 不同），用于构造 skill 引用。
  const skillIdRef = useRef<string | null>(null);

  const fetchList = useCallback(async () => {
    if (!appUserId) return;
    try {
      const res = await listDecks(appUserId);
      setDecks(res.data);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [appUserId]);

  useEffect(() => {
    void fetchList();
    const id = setInterval(() => void fetchList(), 30000);
    return () => clearInterval(id);
  }, [fetchList]);

  // 确保配套 skill 安装到「当前 workspace」，并让 UI 反映真实安装状态。
  //
  // 关键：状态由「真实安装状态」驱动，而非 install 调用的返回值——
  //   1. 先 isArtifactInstalled 复查真实状态：已装直接 ready（不重复安装），
  //      这样用户在别处移除后重新打开 decks 会回到「未安装」而非永久 ready。
  //   2. 未装才 installArtifact 自动装，装完再复查确认，确认已装才 ready。
  //   3. host 尚未上线 isArtifactInstalled 时（查询失败）降级为以 install 结果为准。
  const ensureSkill = useCallback(async () => {
    // 后台静默检查，先不弹窗
    setSkillStatus('checking');

    // 1. 先查真实状态（顺带拿当前环境真实 skill_id）
    const probe = await isArtifactInstalled(SKILL_NAME);
    if (probe.artifactId) skillIdRef.current = probe.artifactId;
    if (probe.success && probe.installed) {
      // 已安装：静默就绪，不弹窗
      setSkillStatus('ready');
      return;
    }

    // 2. 确认未装 → 这时才弹窗 + 安装
    setSkillStatus('installing');
    setSkillModalOpen(true);
    const result = await installArtifact(SKILL_NAME);
    if (result.artifactId) skillIdRef.current = result.artifactId;
    if (!result.success) {
      setSkillStatus('error');
      console.warn('[decks] skill install failed:', result.error);
      return;
    }

    // 3. 装完复查确认真实状态（host 支持时以复查为准，避免误报）
    const verify = await isArtifactInstalled(SKILL_NAME);
    if (verify.artifactId) skillIdRef.current = verify.artifactId;
    if (verify.success) {
      setSkillStatus(verify.installed ? 'ready' : 'error');
      if (!verify.installed) {
        console.warn('[decks] install 返回成功但复查未安装');
      }
    } else {
      // host 不支持 isArtifactInstalled（旧版）→ 降级以 install 结果为准
      setSkillStatus('ready');
    }
    console.log('[decks] skill ready:', result.name ?? SKILL_NAME);
  }, []);

  // 每次进入页面（每次 iframe 加载）都按真实状态复查 —— 不再用永久缓存，
  // 这样用户在别处移除 skill 后重新打开 decks 会显示「未安装」。
  useEffect(() => {
    if (!appUserId) return;
    void ensureSkill();
  }, [appUserId, ensureSkill]);

  // 「准备技能」弹窗随状态联动：
  //   checking — 后台静默，不动弹窗（默认关）
  //   installing/error — 弹出
  //   ready — 若弹窗开着（说明刚装完）停留 1.2s 再关；没开着则不打扰
  useEffect(() => {
    if (skillStatus === 'checking') return;
    if (skillStatus === 'installing' || skillStatus === 'error') {
      setSkillModalOpen(true);
      return;
    }
    if (skillStatus === 'ready') {
      const t = setTimeout(() => setSkillModalOpen(false), 1200);
      return () => clearTimeout(t);
    }
  }, [skillStatus]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  async function handleDelete(deck: DeckMeta, e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!appUserId) return;
    if (!confirm(`确定要删除「${deck.title}」吗？这个动作不可撤销。`)) return;
    try {
      await deleteDeck(appUserId, deck.deck_id);
      showToast('已删除');
      void fetchList();
    } catch (err) {
      showToast(`删除失败：${(err as Error).message}`);
    }
  }

  // 「用这个风格」CTA：把 skill + app 引用（<reference> 文本）+ 填空模板注入输入框
  // 性能：不阻塞等 isArtifactInstalled（dev 上串行 3 请求约 7s）。直接用后台
  // ensureSkill 已缓存的 skillIdRef；未就绪则先传 null（不带 skill 芯片，
  // app_custom 引用仍标识 PPT 任务），保证输入框立即出现。
  const handleAdoptStyle = useCallback(
    async (template: TemplateMeta, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!appUserId) return;
      const text = buildTemplateChatInput(
        template,
        appUserId,
        skillIdRef.current,
      );
      const result = await setChatInput(text);
      showToast(result.success ? '已填好引导，补全后发送' : '插入失败 — 请重试');
    },
    [appUserId],
  );

  const blankAction = useCallback(async () => {
    if (!appUserId) return;
    const text = buildBlankChatInput(appUserId, skillIdRef.current);
    const result = await setChatInput(text);
    showToast(result.success ? '已填好引导，补全后发送' : '插入失败 — 请重试');
  }, [appUserId]);

  const templateCards = useMemo(
    () =>
      TEMPLATES.map((t) => ({
        ...t,
        cover: TEMPLATE_COVERS[t.id] ?? '',
      })),
    [],
  );

  if (!appUserId) return null; // UserGate 兜底

  const hasDecks = decks !== null && decks.length > 0;

  return (
    <div className="page">
      {skillModalOpen && (
        <div className="skill-modal__overlay" role="dialog" aria-modal="true">
          <div className="skill-modal">
            {skillStatus === 'installing' && (
              <>
                <span className="skill-modal__spinner" aria-hidden />
                <h2 className="skill-modal__title">正在准备 PPT 技能</h2>
                <p className="skill-modal__desc">
                  正在为当前空间安装「PPT 制作」技能，稍候片刻…
                </p>
              </>
            )}
            {skillStatus === 'ready' && (
              <>
                <span className="skill-modal__icon skill-modal__icon--ok">✓</span>
                <h2 className="skill-modal__title">技能已就绪</h2>
                <p className="skill-modal__desc">现在可以开始制作 PPT 了</p>
              </>
            )}
            {skillStatus === 'error' && (
              <>
                <span className="skill-modal__icon skill-modal__icon--err">!</span>
                <h2 className="skill-modal__title">技能安装失败</h2>
                <p className="skill-modal__desc">
                  PPT 技能未能安装到当前空间，制作功能可能不可用。
                </p>
                <div className="skill-modal__actions">
                  <button
                    className="btn btn--primary"
                    onClick={() => void ensureSkill()}
                  >
                    重新安装
                  </button>
                  <button
                    className="btn btn--ghost"
                    onClick={() => setSkillModalOpen(false)}
                  >
                    稍后再说
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <header className="page__header">
        <h1 className="page__title">我的 PPT</h1>
        <div className="page__header-actions">
          {skillStatus === 'installing' && (
            <span className="skill-chip skill-chip--installing">
              <span className="skill-chip__spinner" aria-hidden />
              正在准备 PPT 技能…
            </span>
          )}
          {skillStatus === 'ready' && (
            <span className="skill-chip skill-chip--ready">
              ✓ 技能已就绪
            </span>
          )}
          {skillStatus === 'error' && (
            <button
              type="button"
              className="skill-chip skill-chip--error"
              onClick={() => void ensureSkill()}
              title="点击重新安装 PPT 技能到当前空间"
            >
              ⚠ PPT 技能未安装 · 点击安装
            </button>
          )}
          <button className="btn btn--primary" onClick={() => void blankAction()}>
            + 新建 PPT
          </button>
        </div>
      </header>

      <main className="page__body">
        {error && (
          <div className="error-banner">加载失败：{error}</div>
        )}

        {/* 模板示例画廊：始终展示，点封面预览 / 点 CTA 直接发起 */}
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">从风格示例开始</h2>
            <p className="section__sub">
              点封面看完整 demo · 点「用这个风格」直接给小北发请求
            </p>
          </div>
          <div className="template-row">
            {templateCards.map((t) => (
              <div
                key={t.id}
                className="template-tile template-tile--clickable"
                style={{ '--tile-accent': t.accentColor } as React.CSSProperties}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/demo/${encodeURIComponent(t.id)}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/demo/${encodeURIComponent(t.id)}`);
                  }
                }}
              >
                <div className="template-tile__cover">
                  <div
                    className="template-tile__svg"
                    dangerouslySetInnerHTML={{ __html: t.cover }}
                  />
                  <div className="template-tile__hover">
                    <span>看 demo →</span>
                  </div>
                </div>
                <div className="template-tile__meta">
                  <h4 className="template-tile__name">{t.name}</h4>
                  <p className="template-tile__usecase">{t.useCase}</p>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm template-tile__cta"
                    onClick={(e) => void handleAdoptStyle(t, e)}
                  >
                    用这个风格
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 用户的 deck 列表 */}
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">我的 PPT</h2>
            {hasDecks && (
              <p className="section__sub">{decks!.length} 份 — 点击查看，下载 .pptx 即可在 PowerPoint 中编辑</p>
            )}
          </div>

          {decks === null && !error && <p className="muted">加载中…</p>}

          {decks !== null && !hasDecks && (
            <div className="empty empty--inline">
              <p className="empty__sub">
                还没有 PPT — 点上方一张模板示例开始，或者点右上「+ 新建 PPT」自由描述需求
              </p>
            </div>
          )}

          {hasDecks && (
            <div className="deck-grid">
              {decks!.map((d) => (
                <Link
                  key={d.deck_id}
                  to={`/deck/${encodeURIComponent(d.deck_id)}`}
                  className="deck-card"
                >
                  <button
                    className="btn btn--ghost btn--sm deck-card__delete"
                    onClick={(e) => handleDelete(d, e)}
                    aria-label="删除"
                  >
                    删除
                  </button>
                  <div className="deck-card__thumbnail">
                    {d.thumbnail_url ? (
                      <img src={d.thumbnail_url} alt={d.title} loading="lazy" />
                    ) : d.cover_svg ? (
                      <div
                        className="deck-card__thumbnail-svg"
                        dangerouslySetInnerHTML={{ __html: d.cover_svg }}
                      />
                    ) : (
                      <span className="deck-card__thumbnail-placeholder">
                        暂无缩略图
                      </span>
                    )}
                  </div>
                  <div className="deck-card__meta">
                    <h4 className="deck-card__title">{d.title}</h4>
                    <div className="deck-card__bottom">
                      <span className="template-chip">
                        {TEMPLATES.find((t) => t.id === d.template)?.name ??
                          d.template}
                      </span>
                      <span>{d.page_count} 页</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
